import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
  { id: 'reminder', label: 'First reminder', note: 'Sent 7 days after a single-item purchase' },
  { id: 'nudge', label: 'Final nudge', note: 'Sent 3 days before the personal link expires (day 27), same link' },
];

const Preview = ({ kind, tpl, authHeaders }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      axios.post(`${API}/admin/upgrade-emails/preview`, { kind, ...tpl }, authHeaders)
        .then((r) => { setPreview(r.data); setError(''); })
        .catch((e) => setError(e?.response?.data?.detail || 'Preview unavailable'));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [kind, tpl.subject, tpl.intro]);
  return (
    <div data-testid={`upgrade-email-preview-${kind}`}>
      {error && <p className="text-sm text-red-600 mb-2" data-testid={`upgrade-email-error-${kind}`}>{error}</p>}
      {preview && (
        <>
          <p className="text-sm text-[#1E1622]/70 mb-2"><span className="font-medium">Subject:</span> <span data-testid={`upgrade-email-preview-subject-${kind}`}>{preview.subject}</span></p>
          <iframe title={`${kind} preview`} srcDoc={preview.html} sandbox="" className="w-full h-96 border border-[#E6DAC5] rounded-xl bg-white" />
        </>
      )}
    </div>
  );
};

export const UpgradeEmailsEditor = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('reminder');
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line
  useEffect(() => { axios.get(`${API}/admin/upgrade-emails`, authHeaders).then((r) => setData(r.data)).catch(() => toast.error('Could not load upgrade emails')); }, []);
  if (!data) return <Loader2 className="animate-spin text-[#3B164C]" />;

  const tpl = data.templates[kind];
  const set = (field, value) => setData((d) => ({ ...d, templates: { ...d.templates, [kind]: { ...d.templates[kind], [field]: value } } }));
  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/upgrade-emails`, data.templates, authHeaders); toast.success('Upgrade emails saved for all future reminders'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    setSaving(false);
  };
  const reset = () => setData((d) => ({ ...d, templates: { ...d.templates, [kind]: { ...d.defaults[kind] } } }));

  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="upgrade-emails-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Upgrade emails</h3>
        <div className="flex gap-2">
          {KINDS.map((k) => (
            <button key={k.id} onClick={() => setKind(k.id)} data-testid={`upgrade-email-tab-${k.id}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${kind === k.id ? 'bg-[#3B164C] text-white border-[#3B164C]' : 'border-[#E6DAC5] text-[#1E1622]/70 hover:border-[#C5A059]'}`}>{k.label}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#1E1622]/50 mb-5">{KINDS.find((k) => k.id === kind).note}. Placeholders: {data.placeholders.map((p) => `{${p}}`).join(' ')}</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1E1622] mb-2">Subject</label>
            <input value={tpl.subject} onChange={(e) => set('subject', e.target.value)} maxLength={200} data-testid={`upgrade-email-subject-${kind}`}
              className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1E1622] mb-2">Opening paragraph</label>
            <textarea value={tpl.intro} onChange={(e) => set('intro', e.target.value)} rows={8} maxLength={2000} data-testid={`upgrade-email-intro-${kind}`}
              className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="upgrade-email-save" className="btn-primary text-sm inline-flex items-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save both emails
            </button>
            <button onClick={reset} data-testid={`upgrade-email-reset-${kind}`} className="text-sm text-[#1E1622]/60 hover:text-[#3B164C] inline-flex items-center gap-1"><RotateCcw size={14} /> Reset to default</button>
          </div>
          <p className="text-xs text-[#1E1622]/50">The preview uses sample figures (Vol I bought for A$22). Buyers see their own amounts.</p>
        </div>
        <Preview kind={kind} tpl={tpl} authHeaders={authHeaders} />
      </div>
    </div>
  );
};
